import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import {
  leagueCustomerAdministratorInviteRoute,
  leagueCustomerAdministratorRoute,
  leagueCustomerAdministratorsRoute,
} from "network/api-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import { useGestionAccess } from "hooks/useGestionAccess";
import GestionAdministratorInviteDialog from "components/pages/Gestion/GestionAdministratorInviteDialog";
import {
  ADMIN_ROLES,
  adminErrorMessageId,
  adminRoleLabelId,
} from "components/pages/Gestion/gestion-administrators-helpers";

const fullName = (person) => `${person?.first_name ?? ""} ${person?.last_name ?? ""}`.trim();

/**
 * Ajustes > Administradores: who can manage this Gestión customer, with role and area checkmarks.
 * Only managers and Reva admins load it; the API enforces the same rule.
 */
const GestionAdministrators = () => {
  const intl = useIntl();
  const { user } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const { customerId } = useGestionCustomer();
  const access = useGestionAccess();
  const allowed = Boolean(access.manager || access.revaAdmin);

  const [rows, setRows] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    if (!allowed || !customerId) return undefined;

    let cancelled = false;
    setLoading(true);

    performRequest("GET", leagueCustomerAdministratorsRoute(customerId), {}, true, user)
      .then((response) => {
        if (cancelled) return;
        setRows(response.data?.administrators ?? []);
        setPending(response.data?.pending_invites ?? []);
      })
      .catch(() => {
        if (!cancelled)
          enqueueSnackbar(intl.formatMessage({ id: "gestion.error.generic" }), {
            variant: "error",
          });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [allowed, customerId, user, enqueueSnackbar, intl]);

  if (access.loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!allowed) {
    return (
      <Box sx={{ py: 4 }}>
        <RevaText variant="h5" sx={{ mb: 1 }}>
          {intl.formatMessage({ id: "gestion.access.forbidden.title" })}
        </RevaText>
        <RevaText variant="body1" color="text.secondary">
          {intl.formatMessage({ id: "gestion.access.forbidden.body" })}
        </RevaText>
      </Box>
    );
  }

  const errorText = (error) =>
    intl.formatMessage({
      id: adminErrorMessageId(error) ?? "gestion.error.generic",
    });

  const patchRow = (row, body) => {
    setSavingId(row.user_id);
    performRequest("PATCH", leagueCustomerAdministratorRoute(customerId, row.user_id), body, true, user)
      .then((response) => {
        setRows((current) => current.map((item) => (item.user_id === row.user_id ? response.data : item)));
        enqueueSnackbar(intl.formatMessage({ id: "gestion.admins.saved" }), {
          variant: "success",
        });
      })
      .catch((error) => enqueueSnackbar(errorText(error), { variant: "error" }))
      .finally(() => setSavingId(null));
  };

  const confirmRemove = () => {
    const row = removeTarget;
    setRemoving(true);
    performRequest("DELETE", leagueCustomerAdministratorRoute(customerId, row.user_id), {}, true, user)
      .then(() => {
        setRows((current) => current.filter((item) => item.user_id !== row.user_id));
        setRemoveTarget(null);
        enqueueSnackbar(intl.formatMessage({ id: "gestion.admins.removed" }), {
          variant: "success",
        });
      })
      .catch((error) => enqueueSnackbar(errorText(error), { variant: "error" }))
      .finally(() => setRemoving(false));
  };

  const cancelInvite = (invite) => {
    performRequest("DELETE", leagueCustomerAdministratorInviteRoute(customerId, invite.invite_id), {}, true, user)
      .then(() => {
        setPending((current) => current.filter((item) => item.invite_id !== invite.invite_id));
        enqueueSnackbar(intl.formatMessage({ id: "gestion.admins.pending.cancelled" }), { variant: "success" });
      })
      .catch((error) => enqueueSnackbar(errorText(error), { variant: "error" }));
  };

  const handleInvited = (data) => {
    setRows(data?.administrators ?? []);
    setPending(data?.pending_invites ?? []);
  };

  const managerOnlyCheck = (row) => row.is_owner || row.gestion_role === "manager";

  const renderRole = (row) => {
    if (row.locked) {
      return (
        <Chip
          size="small"
          label={intl.formatMessage({
            id: adminRoleLabelId(row.is_owner ? "owner" : row.gestion_role),
          })}
        />
      );
    }

    return (
      <TextField
        select
        size="small"
        value={row.gestion_role}
        onChange={(event) => patchRow(row, { gestion_role: event.target.value })}
        disabled={row.is_self || savingId === row.user_id}
        SelectProps={{ native: true }}
        inputProps={{
          "data-testid": `admin-role-${row.user_id}`,
          "aria-label": intl.formatMessage({ id: "gestion.admins.col.role" }),
        }}
      >
        {ADMIN_ROLES.map((role) => (
          <option key={role} value={role}>
            {intl.formatMessage({ id: adminRoleLabelId(role) })}
          </option>
        ))}
      </TextField>
    );
  };

  const renderAreaCheckbox = (row, flag, testIdPrefix) => (
    <Checkbox
      size="small"
      checked={Boolean(row[flag])}
      disabled={row.locked || row.is_self || row.gestion_role === "manager" || savingId === row.user_id}
      onChange={(event) => patchRow(row, { [flag]: event.target.checked })}
      inputProps={{ "data-testid": `${testIdPrefix}-${row.user_id}` }}
    />
  );

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <RevaText variant="h5">{intl.formatMessage({ id: "gestion.admins.title" })}</RevaText>
          <RevaText variant="body2" color="text.secondary">
            {intl.formatMessage({ id: "gestion.admins.subtitle" })}
          </RevaText>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setInviteOpen(true)}>
          {intl.formatMessage({ id: "gestion.admins.invite" })}
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{intl.formatMessage({ id: "gestion.admins.col.user" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "gestion.admins.col.phone" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "gestion.admins.col.role" })}</TableCell>
                <TableCell align="center">{intl.formatMessage({ id: "gestion.admins.col.finance" })}</TableCell>
                <TableCell align="center">{intl.formatMessage({ id: "gestion.admins.col.tournaments" })}</TableCell>
                <TableCell align="center">{intl.formatMessage({ id: "gestion.admins.col.orgAdmin" })}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <RevaText color="text.secondary">{intl.formatMessage({ id: "gestion.admins.empty" })}</RevaText>
                  </TableCell>
                </TableRow>
              ) : null}
              {rows.map((row) => (
                <TableRow key={row.user_id}>
                  <TableCell>
                    <RevaText sx={{ fontWeight: 600 }}>{fullName(row)}</RevaText>
                    <RevaText variant="body2" color="text.secondary">
                      {row.email}
                    </RevaText>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {row.is_self ? (
                        <Chip
                          size="small"
                          label={intl.formatMessage({
                            id: "gestion.admins.you",
                          })}
                        />
                      ) : null}
                      {row.is_owner ? (
                        <Chip
                          size="small"
                          label={intl.formatMessage({
                            id: "gestion.admins.role.owner",
                          })}
                        />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>{row.phone || "—"}</TableCell>
                  <TableCell>{renderRole(row)}</TableCell>
                  <TableCell align="center">{renderAreaCheckbox(row, "can_finance", "admin-finance")}</TableCell>
                  <TableCell align="center">
                    {renderAreaCheckbox(row, "can_tournaments", "admin-tournaments")}
                  </TableCell>
                  <TableCell align="center">
                    {managerOnlyCheck(row) ? (
                      <Tooltip
                        title={intl.formatMessage({
                          id: "gestion.admins.managerOnly",
                        })}
                      >
                        <CheckIcon fontSize="small" data-testid={`admin-org-${row.user_id}`} />
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {!row.locked && !row.is_self ? (
                      <Tooltip
                        title={intl.formatMessage({
                          id: "gestion.admins.remove",
                        })}
                      >
                        <IconButton
                          size="small"
                          aria-label={intl.formatMessage({
                            id: "gestion.admins.remove",
                          })}
                          onClick={() => setRemoveTarget(row)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {pending.length > 0 ? (
        <Stack spacing={1} sx={{ mt: 3 }}>
          <RevaText variant="h6">{intl.formatMessage({ id: "gestion.admins.pending.title" })}</RevaText>
          {pending.map((invite) => (
            <Stack key={invite.invite_id} direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <RevaText>{fullName(invite) || invite.email}</RevaText>
                <RevaText variant="body2" color="text.secondary">
                  {[
                    invite.email,
                    intl.formatMessage({
                      id: adminRoleLabelId(invite.gestion_role),
                    }),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </RevaText>
                {invite.expires_at ? (
                  <RevaText variant="body2" color="text.secondary">
                    {intl.formatMessage(
                      { id: "gestion.admins.pending.expires" },
                      { date: intl.formatDate(invite.expires_at) },
                    )}
                  </RevaText>
                ) : null}
              </Box>
              <Button size="small" onClick={() => cancelInvite(invite)}>
                {intl.formatMessage({ id: "gestion.admins.pending.cancel" })}
              </Button>
            </Stack>
          ))}
        </Stack>
      ) : null}

      <Dialog open={Boolean(removeTarget)} onClose={() => !removing && setRemoveTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {intl.formatMessage(
            { id: "gestion.admins.remove.title" },
            { name: fullName(removeTarget) || removeTarget?.email || "" },
          )}
        </DialogTitle>
        <DialogContent dividers>
          <RevaText variant="body2">{intl.formatMessage({ id: "gestion.admins.remove.body" })}</RevaText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveTarget(null)} disabled={removing}>
            {intl.formatMessage({ id: "Cancelar" })}
          </Button>
          <Button color="error" variant="contained" onClick={confirmRemove} disabled={removing}>
            {intl.formatMessage({ id: "gestion.admins.remove.confirm" })}
          </Button>
        </DialogActions>
      </Dialog>

      <GestionAdministratorInviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        customerId={customerId}
        onInvited={handleInvited}
      />
    </Box>
  );
};

export default GestionAdministrators;
